import styled from 'styled-components';
import { cssSpacing } from '@spotify-internal/encore-web';
import { sendEvent } from '@spotify-internal/s4mp-page-analytics';
import { ApprovalStatus } from '@hooks/useAlbumInfo/types';
import { useApprovalStatusCopy } from '@hooks/useApprovalStatusCopy';
import { useProfileProtectionsCopy } from '@hooks/useProfileProtectionsCopy';
import { useProfileProtectionExtendedBeta } from '@hooks/useProfileProtectionExtendedBeta';
import { FilterItem } from '@helpers/filterItem';
import { EVENT_ACTION, EVENT_CATEGORY } from '@lib/ga/types';
import { GA_PRODUCT_CATALOG } from '@helpers/constants';
import { FilterDropdown } from '@components/shared/FilterDropdown';

const FilterContainer = styled.div`
  margin-bottom: ${cssSpacing('base')};
`;

interface ApprovalStatusFilterProps {
  selectedStatuses: ApprovalStatus[];
  onApply: (selectedStatuses: ApprovalStatus[]) => void;
}

const FILTER_ITEMS: ApprovalStatus[] = [
  ApprovalStatus.APPROVED,
  ApprovalStatus.DECLINED,
  ApprovalStatus.PARTIALLY_APPROVED,
  ApprovalStatus.NEEDS_APPROVAL,
  ApprovalStatus.EXPIRED,
  ApprovalStatus.NOT_REQUIRED,
];

export const ApprovalStatusFilter = ({
  selectedStatuses,
  onApply,
}: ApprovalStatusFilterProps) => {
  const copy = useApprovalStatusCopy();
  const protectionsCopy = useProfileProtectionsCopy();
  const { isExtendedBetaEnabled } = useProfileProtectionExtendedBeta();

  const labelMap: Record<string, string> = {
    [ApprovalStatus.APPROVED]: copy.approved,
    [ApprovalStatus.DECLINED]: copy.declined,
    [ApprovalStatus.PARTIALLY_APPROVED]: copy.partiallyApproved,
    [ApprovalStatus.NEEDS_APPROVAL]: copy.needsApproval,
    [ApprovalStatus.EXPIRED]: copy.expired,
    [ApprovalStatus.NOT_REQUIRED]: copy.noApprovalNeeded,
  };

  const activeFilterItems = isExtendedBetaEnabled
    ? FILTER_ITEMS.filter(s => s !== ApprovalStatus.NOT_REQUIRED)
    : FILTER_ITEMS;

  const filterItems: FilterItem[] = activeFilterItems.map(status => ({
    id: status,
    label: labelMap[status],
  }));

  return (
    <FilterContainer>
      <FilterDropdown
        label={protectionsCopy.approvalStatus}
        aria-label={protectionsCopy.filterByApprovalStatus}
        name="filter-by-approval-status"
        items={filterItems}
        selectedValues={selectedStatuses as string[]}
        onApply={values => {
          sendEvent({
            eventCategory: EVENT_CATEGORY.PROFILE_PROTECTIONS_FILTER,
            eventAction: EVENT_ACTION.FILTER,
            eventLabel: `Filter applied: ${values.join(', ') || 'All'}`,
            product: GA_PRODUCT_CATALOG,
          });
          onApply(values as ApprovalStatus[]);
        }}
        helpText={protectionsCopy.filterHelpText}
      />
    </FilterContainer>
  );
};
